import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import { api_updateItem } from '@mimir/client-common/util/api';
import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import type { ElectronContext } from '@mimir/common/context/applicationContext';
import type { ItemUpdateRequestDto } from '@mimir/common/dto/searchResultHit';
import { CommonProgressStatus } from '@mimir/common/dto/itemModelPrimitives';
import { ProgressStep } from '@mimir/common/dto/progress';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import { doesPathExist, getBasenameWithoutExtension, join, makeDirPAsync, readFileAsync, removeFileWithRetry, rename, writeFileAsync } from '@mimir/common/util/fs';
import { parseOrNull, replaceAll } from '@mimir/common/util/general';
import { invariant } from '@mimir/common/util/invariant';
import { getGlobalStore } from '@mimir/web/store';
import { deleteKeldaRequests } from '@mimir/web/api/globalClientKelda';
import { downloadPresetIfNotAvailable } from '../../premiere/presets';
import { strictlyTerminateProcess, terminateProcess } from './process';
import type { Logger } from '@mimir/common/types/logger';
import type { ProgressInfo } from '@mimir/client-common/model/encoderExporterTypes';
import type { LauncherConfig } from './config';
import type { LauncherParams } from './launcher-params';
import { getProgressFilePath } from '@mimir/client-common/util/encoderLauncher';
import type { LimitedNodeApi } from '@mimir/common/nodejs/limitedNodeApi/nodeApi';

const postRenderProgressToItem = async (logger: Logger, encoderProgressEvent: ProgressInfo) => {
  const updates: ItemUpdateRequestDto = {
    progressInfo: {
      status: ProgressStep.RENDERING,
    }
  };

  if (encoderProgressEvent.status === 'retryableFailure') {
    // This can happen if Kelda removes the prproj before encoder can start processing it.
    // This should be just retried by the usual way and no progress should be reported
    // from here.
    logger.info(`Ignoring retryable failure for ${encoderProgressEvent.itemId}: ${encoderProgressEvent.log}.`);
    return;
  } else if (encoderProgressEvent.status === 'failed') {
    // Cancel the conform
    logger.error(`Encoder failed for item ${encoderProgressEvent.itemId}. Cancelling conform.`);
    await deleteKeldaRequests(encoderProgressEvent.itemId);

    updates.progressInfo!.status = CommonProgressStatus.ERROR;
    updates.progressInfo!.description = 'Encoder rendering failed';
    updates.progressInfo!.percentage = undefined;
  } else if (encoderProgressEvent.progress) {
    if (encoderProgressEvent.status === 'audioProgress') {
      updates.progressInfo!.status = ProgressStep.PREPARING_AUDIO;
    }
    updates.progressInfo!.percentage = encoderProgressEvent.progress * 100;
  }

  if (encoderProgressEvent.log) {
    try {
      const errorLog = JSON.parse(encoderProgressEvent.log).error;
      if (errorLog) {
        updates.warnings = {
          warnings: [errorLog],
        };
      }
    } catch (err) {
      // suppress
    }
  }

  return api_updateItem(globalBackendClient, encoderProgressEvent.itemId, updates).catch((ex: any) => logger.error(ex));
};

const JSON_PARSING_ERROR = 'Unable to parse progressData';

/**
 * Reads progress/error/completion info from the given disk file every second and posts the update
 * to the item.
 */
export const waitForJob = async (logger: Logger, electronContext: ElectronContext, progressFilePath: string, timeout: number) => {
  let lastProgressData = '';
  let lastProgressDataSameForTimes = 0;
  let errorReadingProgressForTimes = 0;
  let waitResultMessage = `Wait timed out after ${timeout}s of inactivity from encoder`;
  let timeOfLastActivity = new Date().getTime();
  let msSinceLastActivity = 0;
  const timeoutMs = timeout * 1000;
  do {
    try {
      const progressData = await readFileAsync(electronContext.nodeApi, progressFilePath, 'utf8') as string;

      // We need to keep track if we are getting the same progress many times in a row, in which case we decide that AME is stuck.
      // Otherwise, we need to reset the count so that they do not accumulate over discontinuous but short enough pauses to make us
      // falsely think that encoder is stuck.
      if (progressData === lastProgressData) {
        lastProgressDataSameForTimes++;
      } else {
        // If we get new progress data which is different from the last one, we can be sure that encoder is not stuck
        // and we can reset our bookkeeping about last activity.
        lastProgressDataSameForTimes = 0;
        timeOfLastActivity = new Date().getTime();
      }
      lastProgressData = progressData;

      const progressJSON = parseOrNull<ProgressInfo>(logger, progressData);
      if (progressJSON === null) {
        throw new Error(JSON_PARSING_ERROR);
      }

      // If nothing thrown from readFileAsync or from parseOrNull, we can reset the error count.
      errorReadingProgressForTimes = 0;

      await postRenderProgressToItem(logger, progressJSON);
      if (progressJSON.status === 'complete' || progressJSON.status === 'failed' || progressJSON.status === 'retryableFailure') {
        waitResultMessage = `Job status: ${progressJSON.status}`;
        break;
      }
    } catch (err: any) {
      const errString = `${err}`;
      if (errString.includes('ENOENT')) {
        errorReadingProgressForTimes++;
      } else if (errString.includes(JSON_PARSING_ERROR)) {
        // Logs from customers indicate that we can sometimes get this. Perhaps when the progress JSON file
        // is being overwritten by the encoder exporter script, this driver code is trying to read it at the same
        // time and the read returns with a partial/truncated JSON string. So we need to log and treat this
        // error as retryable.
        logger.warn(`Error while parsing json (${errString}). lastProgressData: ${lastProgressData}`);
        errorReadingProgressForTimes++;
      } else {
        logger.error(`Error while reading progress: ${errString}`);
        waitResultMessage = 'Error while reading progress';
        break;
      }
    }
    await asyncTimeout(1000);

    msSinceLastActivity = (new Date().getTime()) - timeOfLastActivity;
  } while (msSinceLastActivity < timeoutMs);

  logger.info(`lastProgressDataSameForTimes: ${lastProgressDataSameForTimes}, errorReadingProgressForTimes: ${errorReadingProgressForTimes}`);
  return waitResultMessage;
};

export const prepareLauncherParams = async (
  launcherConfig: LauncherConfig,
  launcherParams: LauncherParams,
  services: {
    applicationContext: ElectronContext,
    logger: Logger,
    getDownloadPath: () => string,
  },
): Promise<boolean> => {
  invariant(launcherParams.projectPath, 'prepareLauncherParams: projectPath not found');
  const pathModule = services.applicationContext.nodeApi.path;
  launcherParams.folderPath = pathModule.dirname(launcherParams.projectPath);

  // Since there is a prproj to process, let's now fetch the preset. We assume
  // that the preset name is the same as the folder name.
  const presetName = pathModule.basename(launcherParams.folderPath);
  // Refresh tenant config from server to check if the preset has been added since last fetched.
  await getGlobalStore().dispatch('loadTenantConfig');
  const preset = (getGlobalStore().getters.tenantConfig as TenantConfigDto).presets?.find(p => launcherConfig.doesPresetMatch(presetName, p));
  if (!preset) {
    services.logger.error(`Preset ${presetName} not found in tenant config. Skipping.`);
    return false;
  }

  // Download the preset if not already there
  const presetPath = await downloadPresetIfNotAvailable(services, services.getDownloadPath(), preset);
  if (!presetPath) {
    services.logger.error(`Preset ${presetName} could not found be downloaded. Skipping.`);
    return false;
  }

  // Get project filename
  const projectFileName = pathModule.basename(launcherParams.projectPath);
  services.logger.info(`processFolder: project found - ${projectFileName}`);

  // Set output path
  const itemId = getBasenameWithoutExtension(services.applicationContext.nodeApi, projectFileName);

  const outputDir = join(services.applicationContext.nodeApi, launcherParams.folderPath, 'Output');
  await makeDirPAsync(services.applicationContext.nodeApi, outputDir);
  const outputPath = join(services.applicationContext.nodeApi, outputDir, itemId);
  launcherParams.progressFilePath = getProgressFilePath(launcherParams.projectPath);
  await removeFileWithRetry(services.applicationContext.nodeApi, services.logger, launcherParams.progressFilePath, { maxRetries: 10, retryDelay: 200, suppressLogs: true, });

  // Write the exporter script
  const script = await launcherConfig.getScriptString(services.applicationContext, launcherConfig.encoderExePath, itemId, launcherParams.projectPath, presetPath, outputPath, launcherParams.progressFilePath);

  // Write temp script file
  launcherParams.tempScriptFilePath = join(services.applicationContext.nodeApi, services.applicationContext.mainRequire('os').tmpdir(), `tempScriptFile.${launcherConfig.scriptFileExtension}`);
  await writeFileAsync(services.applicationContext.nodeApi, launcherParams.tempScriptFilePath, script);
  return true;
};

const movePrproj = async (logger: Logger, nodeApi: LimitedNodeApi, projectPath: string) => {
  try {
    // Mimic AME by moving the processed prproj to directory named with current date+time like `../Source/10-30-2023 11-53-31 AM/`
    const sourceDirNow = replaceAll(replaceAll(replaceAll((new Date()).toLocaleString('en-US'), ',', ''), '/', '-'), ':', '-');
    const pathModule = nodeApi.path;
    const projectDirPath = pathModule.dirname(projectPath);
    const sourceDir = join(nodeApi, projectDirPath, 'Source', sourceDirNow);
    await makeDirPAsync(nodeApi, sourceDir);
    const projectFileName = pathModule.basename(projectPath);
    await rename(nodeApi, projectPath, join(nodeApi, sourceDir, projectFileName));

    const objectMasksFolderName = `${pathModule.basename(projectPath, pathModule.extname(projectPath))} Masks`;
    const objectMasksFolderPath = join(nodeApi, projectDirPath, objectMasksFolderName);
    if (await doesPathExist(nodeApi, logger, objectMasksFolderPath, true)) {
      await rename(nodeApi, objectMasksFolderPath, join(nodeApi, sourceDir, objectMasksFolderName));
    }
  } catch (err) {
    // Suppress the error that happens when Kelda removes the prproj already, thinking AME has not picked it up yet.
    const errString = `${err}`;
    if (!errString.includes('ENOENT: no such file or directory, rename')) {
      logger.error(`Error in movePrproj: ${err}`);
    }
  }
};

// Remove temp files etc and do not throw any errors.
export const cleanUpSafely = async (logger: Logger, launcherParams: LauncherParams, electronContext: ElectronContext) => {
  // It's important to delete the progress file with retry, since failure to do so can delay the detection
  // of job completion in Kelda (progressFileHeuristicsSayJobIsOver). For the same reason, we are doing it
  // before moving the prproj below, because that can also result in a temporary lock of their common parent
  // directory, resulting in EBUSY when we attempt to delete a file from that directory immediately after
  // that (e.g., the progress file).
  if (launcherParams.needToCleanUp && launcherParams.progressFilePath) {
    await removeFileWithRetry(electronContext.nodeApi, logger, launcherParams.progressFilePath, { maxRetries: 50, retryDelay: 200, suppressLogs: false, });
  }
  // If needed, we move the prproj we just processed to the ../Source folder so that
  // we don't get triggered to process this again
  if (launcherParams.needToCleanUp && launcherParams.projectPath) {
    await movePrproj(logger, electronContext.nodeApi, launcherParams.projectPath);

    // This is for the extra cleanup that DVR needs for the additional -dependencies.json file
    const itemId = getBasenameWithoutExtension(electronContext.nodeApi, launcherParams.projectPath);
    const pathModule = electronContext.nodeApi.path;
    const projectDir = pathModule.dirname(launcherParams.projectPath);
    const dependenciesFilePath = pathModule.join(projectDir, `${itemId}-dependencies.json`);
    await removeFileWithRetry(electronContext.nodeApi, logger, dependenciesFilePath, { maxRetries: 10, retryDelay: 100, suppressLogs: true, });
  }
  if (launcherParams.tempScriptFilePath) {
    await removeFileWithRetry(electronContext.nodeApi, logger, launcherParams.tempScriptFilePath, { maxRetries: 10, retryDelay: 100, suppressLogs: true, });
  }
};

export const executeJob = async (
  projectPath: string,
  services: {
    applicationContext: ElectronContext,
    logger: Logger,
    getDownloadPath: () => string,
  },
  launcherConfig: LauncherConfig,
): Promise<boolean> => {
  let jobDone = false;
  const launcherParams: LauncherParams = {
    encoderExePath: launcherConfig.encoderExePath,
    encoderExeArgs: launcherConfig.encoderExeArgs,
    projectPath
  };
  try {
    // Prepare temporary paths, scripts etc. required for this particular job
    if (!await prepareLauncherParams(launcherConfig, launcherParams, services)) {
      return jobDone;
    }

    // Check and terminate encoder if already running
    await strictlyTerminateProcess(services.applicationContext, launcherConfig.processName);

    // Launch new encoder
    const encoderProcess = launcherConfig.rendererLaunchStep(services.applicationContext, launcherParams);
    invariant(encoderProcess.pid, 'pid not found');

    // Reaching this point means that encoder has had a chance to process the project, and we should move it out
    // regardless of success or failure at this point, so that we do not process this again.
    launcherParams.needToCleanUp = true;

    // Poll for progress
    invariant(launcherParams.progressFilePath, 'progressFilePath not found');
    const ameResult = await waitForJob(services.logger, services.applicationContext, launcherParams.progressFilePath, launcherConfig.timeout);

    // Terminate
    services.logger.info(`Done waiting for ${launcherConfig.processName} (${ameResult}). Terminating ${launcherConfig.processName} process ${encoderProcess.pid}`);

    // Use SIGKILL, otherwise we can get a prompt on Mac asking to upload crash report.
    // Suppress error since we do not have anything else left for this process after this.
    terminateProcess(services.applicationContext, encoderProcess.pid);
    services.logger.info(`Done terminating ${launcherConfig.processName} process ${encoderProcess.pid}`);
    jobDone = true;
  } catch (err) {
    services.logger.error(`Error in processFolder: ${err}`);
  } finally {
    await cleanUpSafely(services.logger, launcherParams, services.applicationContext);
  }

  return jobDone;
};
